// pages/monitor/VideoMonitor.js —— 视频监控：区域筛选 + 多站切换 + 宫格 + 双击放大 + 云台操作盘
// doc01 2.8：默认低码率播放、底部显示站点名称；仅接入摄像头的电站在此展示；
//            支持省/市/区与站点名称筛选、多站快速切换；双击右下角放大图标可放大画面，放大后可平移/缩放。
import React, { useState } from 'react';
import { Select, Input, Button, Segmented, message } from 'antd';
import {
  UpOutlined, DownOutlined, LeftOutlined, RightOutlined,
  ZoomInOutlined, ZoomOutOutlined, CloseOutlined, VideoCameraOutlined, ReloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import PageHead from '../../components/PageHead';
import { VIDEO_CHANNELS, STATIONS, STATION_REGIONS } from '../../config/stationData';

// 有摄像头的站点（按 VIDEO_CHANNELS 出现顺序去重）
const VIDEO_STATIONS = Array.from(new Set(VIDEO_CHANNELS.map((c) => c.station)));
// 站点名 → 行政区划
const REGION_OF = {};
STATIONS.forEach((s) => { REGION_OF[s.name] = STATION_REGIONS[s.id] || {}; });

const PROVINCE_OPTIONS = [{ value: '浙江省', label: '浙江省' }];
const CITY_OPTIONS = [{ value: '嘉兴市', label: '嘉兴市' }];
// 区县下拉按站点行政区划动态生成，避免新增站点后筛选不到
const DISTRICT_OPTIONS = [
  { value: 'all', label: '全部区县' },
  ...Array.from(new Set(Object.values(STATION_REGIONS).map((r) => r.district)))
    .map((d) => ({ value: d, label: d })),
];

const countOf = (station) => VIDEO_CHANNELS.filter((c) => c.station === station).length;

const VideoMonitor = () => {
  // 查询条件（草稿）与已生效条件分离，配合「查询 / 重置」按钮（doc01 2.8 缺陷 67）
  const [draft, setDraft] = useState({ province: '浙江省', city: '嘉兴市', district: 'all', keyword: '' });
  const [applied, setApplied] = useState({ province: '浙江省', city: '嘉兴市', district: 'all', keyword: '' });
  const [station, setStation] = useState(VIDEO_STATIONS[0]);
  const [focus, setFocus] = useState(null);
  const [quality, setQuality] = useState('低码率');
  const [rate, setRate] = useState('实时');

  // 区域 + 名称筛选后的站点列表
  const stationList = VIDEO_STATIONS.filter((name) => {
    const r = REGION_OF[name] || {};
    if (applied.province && r.province !== applied.province) return false;
    if (applied.city && r.city !== applied.city) return false;
    if (applied.district !== 'all' && r.district !== applied.district) return false;
    if (applied.keyword && !name.includes(applied.keyword)) return false;
    return true;
  });

  const doQuery = () => { setApplied(draft); message.success('已按条件查询'); };
  const doReset = () => {
    const blank = { province: '', city: '', district: 'all', keyword: '' };
    setDraft({ province: '浙江省', city: '嘉兴市', district: 'all', keyword: '' });
    setApplied(blank);
    message.success('筛选条件已重置');
  };

  // 当前站点若被筛掉，回退到列表首个
  const activeStation = stationList.includes(station) ? station : (stationList[0] || station);
  const channels = VIDEO_CHANNELS.filter((c) => c.station === activeStation);
  const onlineCount = channels.filter((c) => c.state === '在线').length;

  const ptzBtn = (icon, label) => (
    <Button
      className="video-ptz-btn"
      icon={icon}
      title={label}
      onClick={() => {}}
    />
  );

  return (
    <div>
      <PageHead title="视频监控" desc="接入站点安防/场区摄像头，远程查看现场画面" />

      <div className="sm-bar">
        <span className="sm-bar-label">区域</span>
        <Select value={draft.province} onChange={(v) => setDraft((d) => ({ ...d, province: v }))} style={{ width: 120 }} options={PROVINCE_OPTIONS} />
        <Select value={draft.city} onChange={(v) => setDraft((d) => ({ ...d, city: v }))} style={{ width: 120 }} options={CITY_OPTIONS} />
        <Select value={draft.district} onChange={(v) => setDraft((d) => ({ ...d, district: v }))} style={{ width: 140 }} options={DISTRICT_OPTIONS} />
        <Input
          value={draft.keyword}
          onChange={(e) => setDraft((d) => ({ ...d, keyword: e.target.value }))}
          prefix={<SearchOutlined />}
          placeholder="站点名称"
          style={{ width: 180 }}
          allowClear
        />
        <Button type="primary" onClick={doQuery}>查询</Button>
        <Button onClick={doReset}>重置</Button>
        <Segmented
          value={quality}
          onChange={setQuality}
          options={['低码率', '高码率']}
        />
        <span className="sm-bar-meta">
          站点 {stationList.length} 个 · 当前 {channels.length} 路 · 在线 {onlineCount} 路
        </span>
      </div>

      {/* 多站切换 */}
      <div className="video-stations">
        {stationList.length === 0 ? (
          <span className="video-stations-empty">当前筛选条件下暂无接入摄像头的站点</span>
        ) : null}
        {stationList.map((s) => (
          <button
            key={s}
            type="button"
            className={`video-station-tab${s === activeStation ? ' active' : ''}`}
            onClick={() => { setStation(s); setFocus(null); }}
          >
            {s}
            <span className="video-station-count">{countOf(s)}</span>
          </button>
        ))}
      </div>

      <div className="video-grid">
        {channels.map((c) => {
          const online = c.state === '在线';
          return (
            <div
              key={c.key}
              className={`video-tile${online ? '' : ' offline'}`}
            >
              <div className="video-screen">
                {online ? (
                  <div className="video-screen-grid">
                    <VideoCameraOutlined className="video-screen-ico" />
                  </div>
                ) : (
                  <div className="video-signal-lost">无视频信号</div>
                )}
              </div>
              <div className="video-tile-foot">
                <span className="video-tile-name">{c.name}</span>
                <span className={`dot${online ? ' online' : ' offline'}`} />
                <span className="video-tile-meta">{c.resolution} · {c.ip}</span>
              </div>
              {/* 文档 2.8：双击右下角放大图标放大画面 */}
              <div
                className="video-tile-corner"
                title="双击放大"
                onDoubleClick={(e) => { e.stopPropagation(); setFocus(c); }}
              >
                <ZoomInOutlined />
              </div>
            </div>
          );
        })}
      </div>

      {/* 放大画中画 */}
      {focus ? (
        <div className="video-overlay" onClick={() => setFocus(null)}>
          <div className="video-overlay-body" onClick={(e) => e.stopPropagation()}>
            <div className="video-stage">
              <div className="video-screen-grid">
                <VideoCameraOutlined className="video-screen-ico big" />
              </div>
              <div className="video-stage-tag">
                {focus.station} · {focus.name} · {focus.resolution}
              </div>
              <Button
                className="video-close"
                shape="circle"
                icon={<CloseOutlined />}
                onClick={() => setFocus(null)}
              />
            </div>

            <div className="video-ptz">
              <div className="panel-title"><span>云台控制</span><small>平移 / 缩放</small></div>
              <div className="video-ptz-pad">
                <div />
                {ptzBtn(<UpOutlined />, '上')}
                <div />
                {ptzBtn(<LeftOutlined />, '左')}
                <Button className="video-ptz-btn center" icon={<ReloadOutlined />} title="复位" onClick={() => {}} />
                {ptzBtn(<RightOutlined />, '右')}
                <div />
                {ptzBtn(<DownOutlined />, '下')}
                <div />
              </div>
              <div className="video-ptz-zoom">
                {ptzBtn(<ZoomOutOutlined />, '缩小')}
                <Segmented value={rate} onChange={setRate} options={['实时', '回放']} />
                {ptzBtn(<ZoomInOutlined />, '放大')}
              </div>
              <div className="video-ptz-note">平移 / 缩放待接入真实流控</div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default VideoMonitor;